import { create } from "zustand";

export interface TraceEvent {
  ts: string;
  kind: string;
  title: string;
  data: Record<string, unknown>;
}

export interface StreamState {
  isStreaming: boolean;
  traceEvents: TraceEvent[];
  finalText: string | null;
  error: string | null;
}

interface StreamStore {
  // Per-session streaming state
  streamStateBySession: Record<string, StreamState>;
  // Per-session pending user message (shown optimistically before worklog updates)
  pendingUserMessageBySession: Record<string, string | null>;
  
  // Actions
  setStreaming: (sessionId: string, isStreaming: boolean) => void;
  appendTrace: (sessionId: string, event: TraceEvent) => void;
  setFinalText: (sessionId: string, text: string | null) => void;
  setError: (sessionId: string, error: string | null) => void;
  setPendingUserMessage: (sessionId: string, message: string | null) => void;
  resetStreamState: (sessionId: string) => void;
  getStreamState: (sessionId: string | null) => StreamState;
  getPendingUserMessage: (sessionId: string | null) => string | null;
  isStreamingBySession: () => Record<string, boolean>;
}

const getInitialStreamState = (): StreamState => ({
  isStreaming: false,
  traceEvents: [],
  finalText: null,
  error: null,
});

export const useStreamStore = create<StreamStore>((set, get) => ({
  streamStateBySession: {},
  pendingUserMessageBySession: {},

  setStreaming: (sessionId: string, isStreaming: boolean) => {
    set((state) => {
      const current = state.streamStateBySession[sessionId] || getInitialStreamState();
      return {
        streamStateBySession: {
          ...state.streamStateBySession,
          [sessionId]: {
            ...current,
            isStreaming,
            // Clear error when starting a new stream
            error: isStreaming ? null : current.error,
          },
        },
      };
    });
  },

  appendTrace: (sessionId: string, event: TraceEvent) => {
    set((state) => {
      const current = state.streamStateBySession[sessionId] || getInitialStreamState();
      return {
        streamStateBySession: {
          ...state.streamStateBySession,
          [sessionId]: {
            ...current,
            traceEvents: [...current.traceEvents, event],
          },
        },
      };
    });
  },

  setFinalText: (sessionId: string, text: string | null) => {
    set((state) => {
      const current = state.streamStateBySession[sessionId] || getInitialStreamState();
      return {
        streamStateBySession: {
          ...state.streamStateBySession,
          [sessionId]: {
            ...current,
            isStreaming: false,
            finalText: text,
            error: null,
          },
        },
      };
    });
  },

  setError: (sessionId: string, error: string | null) => {
    set((state) => {
      const current = state.streamStateBySession[sessionId] || getInitialStreamState();
      return {
        streamStateBySession: {
          ...state.streamStateBySession,
          [sessionId]: {
            ...current,
            // Don't modify isStreaming - only setStreaming and setFinalText should control it
            error,
          },
        },
      };
    });
  },

  setPendingUserMessage: (sessionId: string, message: string | null) => {
    set((state) => ({
      pendingUserMessageBySession: {
        ...state.pendingUserMessageBySession,
        [sessionId]: message,
      },
    }));
  },

  resetStreamState: (sessionId: string) => {
    set((state) => {
      const newState = { ...state.streamStateBySession };
      delete newState[sessionId];
      return {
        streamStateBySession: newState,
        pendingUserMessageBySession: {
          ...state.pendingUserMessageBySession,
          [sessionId]: null,
        },
      };
    });
  },

  getStreamState: (sessionId: string | null): StreamState => {
    if (!sessionId) {
      return getInitialStreamState();
    }
    return get().streamStateBySession[sessionId] || getInitialStreamState();
  },

  getPendingUserMessage: (sessionId: string | null): string | null => {
    if (!sessionId) {
      return null;
    }
    return get().pendingUserMessageBySession[sessionId] || null;
  },

  isStreamingBySession: (): Record<string, boolean> => {
    const state = get();
    const result: Record<string, boolean> = {};
    for (const [sessionId, streamState] of Object.entries(state.streamStateBySession)) {
      result[sessionId] = streamState.isStreaming;
    }
    return result;
  },
}));

